import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import productData, { getProductById } from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { formatCurrency, formatStockMessage } from "../utils/formatters";
import { CartContext } from "./CartContext";
import ProductGrid from "./ProductGrid";
import "../css/productdetails.scss";

const productDetails = [
  ["Fit", "fit"],
  ["Weight", "weight"],
  ["Construction", "construction"],
  ["Origin", "origin"],
  ["Care", "care"],
];

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

function ProductDetail() {
  const { id } = useParams();
  const product = getProductById(id);
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState(product?.sizes?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(product?.colors?.[0] || "");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openPanel, setOpenPanel] = useState("details");

  useEffect(() => {
    setSelectedSize(product?.sizes?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    window.scrollTo({ top: 0 });
  }, [product?.id, product?.sizes, product?.colors]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return productData
      .filter(
        (item) =>
          item.id !== product.id &&
          (item.collection === product.collection ||
            item.category === product.category)
      )
      .slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--missing" id="main-content">
        <div className="product-detail__not-found">
          <p className="product-detail__eyebrow">404</p>
          <h1>We could not find that piece.</h1>
          <p>It may have sold through, or the address may have changed.</p>
          <Link onClick={scrollToTop} to="/shop">
            Explore all pieces
          </Link>
        </div>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const onSale =
    Number(product.compareAtPrice) > Number(product.price) &&
    Number(product.compareAtPrice) > 0;
  const saved = isWishlisted(product.id);
  const soldOut = Number(product.stock) <= 0;

  const handleAddToCart = () => {
    if (soldOut) return;
    addToCart(product, {
      quantity,
      size: selectedSize,
      color: selectedColor,
    });
  };

  const togglePanel = (panel) => {
    setOpenPanel((current) => (current === panel ? "" : panel));
  };

  return (
    <main className="product-detail" id="main-content">
      <nav className="product-detail__breadcrumb" aria-label="Breadcrumb">
        <Link to="/shop">Shop</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/shop?collection=${product.collection}`}>
          {product.collection}
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="product-detail__container">
        <section className="gallery-column" aria-label={`${product.name} images`}>
          <div className="main-image">
            <img
              src={images[selectedImage]}
              alt={`${product.name}, view ${selectedImage + 1} of ${images.length}`}
              decoding="async"
            />
            <div className="main-image__counter" aria-hidden="true">
              {String(selectedImage + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
            </div>
          </div>

          <div className="thumbs" role="list" aria-label="Choose product image">
            {images.map((image, index) => (
              <button
                key={image}
                className={`thumb${index === selectedImage ? " active" : ""}`}
                type="button"
                onClick={() => setSelectedImage(index)}
                aria-label={`Show view ${index + 1}`}
                aria-pressed={index === selectedImage}
              >
                <img src={image} alt="" loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        </section>

        <aside className="product-info">
          <div className="product-meta">
            <p className="product-brand">{product.category}</p>
            <h1 className="product-name">{product.name}</h1>
            <div className="product-price">
              <span>{formatCurrency(product.price)}</span>
              {onSale && <del>{formatCurrency(product.compareAtPrice)}</del>}
            </div>
          </div>

          <div className="product-description">
            {product.description.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          <p className="product-info__material">{product.material}</p>
          <p className={`product-info__stock${product.stock <= 5 ? " is-low" : ""}`}>
            {formatStockMessage(product.stock)}
          </p>

          <div className="product-options">
            <div className="product-option">
              <label htmlFor="product-colour">Colour</label>
              <select
                id="product-colour"
                value={selectedColor}
                onChange={(event) => setSelectedColor(event.target.value)}
              >
                {product.colors.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
            </div>

            <div className="product-option">
              <label htmlFor="product-size">Size</label>
              <select
                id="product-size"
                value={selectedSize}
                onChange={(event) => setSelectedSize(event.target.value)}
              >
                {product.sizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="product-actions">
            <div className="product-quantity" role="group" aria-label="Quantity">
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                disabled={quantity === 1}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((current) => Math.min(product.stock, current + 1))}
                disabled={quantity >= product.stock}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
            <button
              className="add-to-cart"
              type="button"
              onClick={handleAddToCart}
              disabled={soldOut}
            >
              {soldOut ? "Sold out" : "Add to bag"}
            </button>
            <button
              className={`product-wishlist${saved ? " is-active" : ""}`}
              type="button"
              onClick={() => toggleWishlist(product.id)}
              aria-label={`${saved ? "Remove" : "Save"} ${product.name} ${saved ? "from" : "to"} favourites`}
              aria-pressed={saved}
            >
              <span aria-hidden="true">{saved ? "♥" : "♡"}</span>
            </button>
          </div>

          <div className="product-accordions">
            <section className="product-accordion">
              <button
                type="button"
                aria-expanded={openPanel === "details"}
                onClick={() => togglePanel("details")}
              >
                Product details <span aria-hidden="true">{openPanel === "details" ? "−" : "+"}</span>
              </button>
              {openPanel === "details" && (
                <dl>
                  {productDetails.map(([label, key]) => (
                    <div key={key}>
                      <dt>{label}</dt>
                      <dd>{product.attributes[key]}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            <section className="product-accordion">
              <button
                type="button"
                aria-expanded={openPanel === "delivery"}
                onClick={() => togglePanel("delivery")}
              >
                Delivery &amp; returns <span aria-hidden="true">{openPanel === "delivery" ? "−" : "+"}</span>
              </button>
              {openPanel === "delivery" && (
                <div className="product-accordion__copy">
                  <p>
                    Orders are prepared in {siteConfig.shipping.dispatchTime}.
                    Standard delivery starts at {formatCurrency(siteConfig.shipping.standardRate)}.
                  </p>
                  <p>
                    Unworn pieces may be returned within {siteConfig.shipping.returnsWindowDays} days.
                  </p>
                  <Link to="/shipping">Read delivery information</Link>
                </div>
              )}
            </section>
          </div>

          <p className="product-info__sku">
            Ref. BP-{String(product.id).padStart(4, "0")} · Selected in Hong Kong
          </p>
        </aside>
      </div>

      {relatedProducts.length > 0 && (
        <section className="product-related" aria-labelledby="related-products-title">
          <header className="product-related__header">
            <p>You may also like</p>
            <h2 id="related-products-title">More to live in</h2>
          </header>
          <ProductGrid products={relatedProducts} label="Related products" />
        </section>
      )}
    </main>
  );
}

export default ProductDetail;
